import { useCallback, useEffect, useRef, useState } from "react";

/**
 * A hook to fetch async data.
 * @class useAsync
 * @borrows useAsyncObject
 * @param {object} _                props
 * @param {async} _.asyncFunc         Promise like async function
 * @param {bool} _.immediate=false    Invoke the function immediately
 * @param {object} _.funcParams       Function initial parameters
 * @param {object} _.initialData      Initial data
 * @returns {useAsyncObject}        Async object
 * @example
 *   const { execute, loading, data, error } = useAync({
 *    asyncFunc: async () => { return 'data' },
 *    immediate: false,
 *    funcParams: { data: '1' },
 *    initialData: 'Hello'
 *  })
 */

export interface asyncResType<Params, Result> {
  execute: (
    params: Params,
    options?: { throws?: boolean }
  ) => Promise<Result | void>;
  loading: boolean;
  data: Result | undefined;
  error: string | null;
  errorObject: unknown | null;
  params: Params;
  called: boolean;
}

export const useAsync = <F extends (args: any) => Promise<any>>(
  props: {
    asyncFunc: F;
    immediate: boolean;
    funcParams: Parameters<F>[0];
    clearOnError?: boolean;
    canCall?: boolean;
    id?: string;
  },
  changeEffectors?: unknown[]
): asyncResType<Parameters<F>[0], Awaited<ReturnType<F>>> => {
  type Params = Parameters<F>[0];
  type Result = Awaited<ReturnType<F>>;

  const { asyncFunc, immediate, funcParams } = {
    ...props
  };
  const effectors = changeEffectors || [];
  const canCall = props.canCall === undefined ? true : props.canCall;
  const clearOnError = props.clearOnError || false;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Result>();
  const [asyncData, setAyncData] = useState<{
    response?: Result;
    params?: Params;
  }>({});
  const [params, setParams] = useState({ ...funcParams });
  const [called, setCalled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorObject, setErrorObject] = useState<Object | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    const equal = JSON.stringify(params) === JSON.stringify(asyncData.params);
    if (equal) setData(asyncData.response);
  }, [params, asyncData.params, asyncData.response]);

  const errorSetter = (error: { message: any } | null) => {
    if (!error) {
      setError(null);
      setErrorObject(null);
    } else {
      setError(error?.message ? `${error.message}` : "Something went wrong!");
      setErrorObject(Object(error));
    }
  };

  const execute = useCallback(
    async (_params: Params, options?: { throws?: boolean }) => {
      if (canCall) {
        setLoading(true);
        setCalled(true);
        const params = { ...funcParams, ..._params };
        setParams({ ...params });
        errorSetter(null);

        return asyncFunc(params)
          .then((res) => {
            console.log(`async call ...`);
            if (!mountedRef.current) return;

            setAyncData({
              params,
              response: res
            });

            errorSetter(null);
            setLoading(false);
            return res;
          })
          .catch((err) => {
            if (clearOnError) {
              setAyncData({
                params,
                response: undefined
              });
            }
            if (!mountedRef.current) return;
            errorSetter(err);
            setLoading(false);
            console.error(err);
            if (options?.throws) {
              throw Object(err);
            }
          });
      }
    },
    [asyncFunc, canCall, clearOnError, funcParams]
  );

  useEffect(() => {
    mountedRef.current = true;
    if (immediate) {
      execute(funcParams, undefined);
    }

    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...effectors]);

  return {
    execute,
    loading,
    data,
    error,
    errorObject,
    params,
    called
  };
};

export const getDefaultReturn = <T>(params: T) => {
  return {
    execute: async (params: T) => {},
    loading: false,
    data: undefined,
    error: null,
    params,
    errorObject: null,
    called: false
  };
};
